/**
 * tableUtils.js — Reusable sortable-table primitives.
 *
 * Exports:
 *   SortTh          — <th> that cycles asc/desc when clicked
 *   useSortState    — [sortKey, sortDir, onSort] hook
 *   applySortStr    — sorts an array by a key
 *   useColFilters   — per-column filter state hook
 *   applyColFilters — applies per-column filters to a list
 *   FilterCell      — <td> filter input matching a column type
 */

import React, { useState } from 'react';

// ─── SortTh ──────────────────────────────────────────────────────────────────
export function SortTh({ col, sortKey, sortDir, onSort, className, children, style }) {
  const active = sortKey === col;
  return (
    <th
      className={`sq__sort-th${active ? ' sq__sort-active' : ''}${className ? ' ' + className : ''}`}
      style={style}
      onClick={() => onSort(col)}
    >
      <span className="sq__sort-label">{children}</span>
      <span className="sq__sort-icon">{active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
    </th>
  );
}

// ─── useSortState ─────────────────────────────────────────────────────────────
export function useSortState(defaultKey, defaultDir = 'desc') {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);
  const onSort = col => {
    if (col === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(col); setSortDir('desc'); }
  };
  return [sortKey, sortDir, onSort];
}

// ─── applySortStr ─────────────────────────────────────────────────────────────
export function applySortStr(list, key, dir) {
  return [...list].sort((a, b) => {
    const av = a[key] ?? '', bv = b[key] ?? '';
    const n = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return dir === 'asc' ? n : -n;
  });
}

// ─── useColFilters ────────────────────────────────────────────────────────────
export function useColFilters(cols) {
  const init = {};
  cols.forEach(c => {
    if (c.type === 'minmax') init[c.key] = { min: '', max: '' };
    else if (c.type === 'namefilter') init[c.key] = { value: '', mode: 'contains', caseSensitive: false };
    else init[c.key] = { value: '' };
  });
  const [filters, setFilters] = useState(init);
  const setFilter = (key, field, val) =>
    setFilters(prev => ({ ...prev, [key]: { ...prev[key], [field]: val } }));
  const clearFilters = () => setFilters(init);
  const hasActive = Object.values(filters).some(f =>
    (f.value !== undefined && f.value !== '') || (f.min !== '' || f.max !== '')
  );
  return [filters, setFilter, clearFilters, hasActive];
}

// ─── applyColFilters ──────────────────────────────────────────────────────────
export function applyColFilters(list, filters, colDefs) {
  return list.filter(row =>
    colDefs.every(col => {
      const f = filters[col.key];
      if (!f) return true;
      if (col.type === 'namefilter') {
        if (!f.value) return true;
        const haystack = f.caseSensitive ? String(row[col.key] ?? '') : String(row[col.key] ?? '').toLowerCase();
        const needle   = f.caseSensitive ? f.value : f.value.toLowerCase();
        const found = haystack.includes(needle);
        return f.mode === 'notcontains' ? !found : found;
      }
      if (col.type === 'text') {
        if (!f.value) return true;
        return String(row[col.key] ?? '').toLowerCase().includes(f.value.toLowerCase());
      }
      if (col.type === 'min') {
        if (f.value === '') return true;
        return (row[col.key] ?? 0) >= parseFloat(f.value);
      }
      if (col.type === 'minmax') {
        const v = row[col.key] ?? 0;
        if (f.min !== '' && v < parseFloat(f.min)) return false;
        if (f.max !== '' && v > parseFloat(f.max)) return false;
        return true;
      }
      return true;
    })
  );
}

// ─── FilterCell ───────────────────────────────────────────────────────────────
export function FilterCell({ type, fval, colKey, setFilter, className }) {
  if (type === 'namefilter')
    return (
      <td className={className} style={{ minWidth: 220 }}>
        <div className="sq__fcol-namefilter">
          <select
            className="sq__fcol-mode"
            value={fval.mode}
            onChange={e => setFilter(colKey, 'mode', e.target.value)}
          >
            <option value="contains">contains</option>
            <option value="notcontains">not contains</option>
          </select>
          <input
            className="sq__fcol-input sq__fcol-nameval"
            type="search"
            placeholder="⌕"
            value={fval.value}
            onChange={e => setFilter(colKey, 'value', e.target.value)}
          />
          <button
            className={`sq__fcol-case${fval.caseSensitive ? ' active' : ''}`}
            title={fval.caseSensitive ? 'Case-sensitive ON' : 'Case-sensitive OFF'}
            onClick={() => setFilter(colKey, 'caseSensitive', !fval.caseSensitive)}
            type="button"
          >
            Aa
          </button>
        </div>
      </td>
    );

  if (type === 'text')
    return (
      <td className={className}>
        <input
          className="sq__fcol-input"
          type="search"
          placeholder="⌕"
          value={fval.value}
          onChange={e => setFilter(colKey, 'value', e.target.value)}
        />
      </td>
    );

  if (type === 'min')
    return (
      <td className={className}>
        <input
          className="sq__fcol-input sq__fcol-num"
          type="number"
          placeholder="≥"
          min={0}
          value={fval.value}
          onChange={e => setFilter(colKey, 'value', e.target.value)}
        />
      </td>
    );

  if (type === 'minmax')
    return (
      <td className={className}>
        <div className="sq__fcol-range">
          <input
            className="sq__fcol-input sq__fcol-num"
            type="number"
            placeholder="min"
            min={0}
            value={fval.min}
            onChange={e => setFilter(colKey, 'min', e.target.value)}
          />
          <input
            className="sq__fcol-input sq__fcol-num"
            type="number"
            placeholder="max"
            min={0}
            value={fval.max}
            onChange={e => setFilter(colKey, 'max', e.target.value)}
          />
        </div>
      </td>
    );

  return <td className={className} />;
}
